import IconHide from '@assets/icons/hide.svg?react';
import IconShow from '@assets/icons/show.svg?react';
import {
 Alert,
 Box,
 Button,
 IconButton,
 TextField,
 type Theme,
 Typography,
} from '@mui/material';
import type { UsersDTO } from '@redux/identity/identityTypes';
import type { RootState } from '@redux/store';
import { updateItemStart } from '@redux/users/usersSlice';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface ChangePasswordState {
 password: string;
 repeatPassword: string;
 hasMismatch: boolean;
 isCheckingPasswords: boolean;
}

const ChangePassword = ({
 item,
 hideButtons = false,
 hideTitle = false,
 isSubmittingOverride,
 onStateChange,
}: {
 item: UsersDTO;
 hideButtons?: boolean;
 hideTitle?: boolean;
 isSubmittingOverride?: boolean;
 onStateChange?: (state: ChangePasswordState) => void;
}) => {
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const { loading } = useSelector((state: RootState) => state.users);
 const [password, setPassword] = useState('');
 const [repeatPassword, setRepeatPassword] = useState('');
 const [showPassword, setShowPassword] = useState(false);
 const [showRepeatPassword, setShowRepeatPassword] = useState(false);
 const [hasMismatch, setHasMismatch] = useState(false);
 const [isCheckingPasswords, setIsCheckingPasswords] = useState(false);

 const isSubmitting = isSubmittingOverride ?? !!loading?.updateItem;
 const hasValues = password.length > 0 && repeatPassword.length > 0;
 const hasImmediateMismatch = hasValues && password !== repeatPassword;
 const isSubmitDisabled =
  isSubmitting ||
  password.length === 0 ||
  repeatPassword.length === 0 ||
  isCheckingPasswords ||
  hasMismatch;

 useEffect(() => {
  if (!hasValues) {
   setHasMismatch(false);
   setIsCheckingPasswords(false);
   onStateChange?.({
    password,
    repeatPassword,
    hasMismatch: false,
    isCheckingPasswords: false,
   });
   return;
  }

  setIsCheckingPasswords(true);
  onStateChange?.({
   password,
   repeatPassword,
   hasMismatch,
   isCheckingPasswords: true,
  });
  const timerId = window.setTimeout(() => {
   const mismatch = password !== repeatPassword;
   setHasMismatch(mismatch);
   setIsCheckingPasswords(false);
   onStateChange?.({
    password,
    repeatPassword,
    hasMismatch: mismatch,
    isCheckingPasswords: false,
   });
  }, 1000);

  return () => {
   window.clearTimeout(timerId);
  };
 }, [hasMismatch, hasValues, onStateChange, password, repeatPassword]);

 const handleCancel = () => {
  setPassword('');
  setRepeatPassword('');
 };

 const handleSubmit = () => {
  if (isSubmitDisabled || hasImmediateMismatch) {
   setHasMismatch(hasImmediateMismatch);
   setIsCheckingPasswords(false);
   return;
  }

  dispatch(
   updateItemStart({
    table: 'users',
    id: item.Id || '',
    data: {
     ...item,
     NewPassword: password,
    },
    isPut: true,
   }),
  );
 };

 return (
  <Box sx={{ mb: 2 }}>
   {!hideTitle && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
     <Typography
      sx={{
       fontSize: 14,
       fontWeight: 500,
       color: (theme) => theme.palette.kemek.darkGray,
      }}
     >
      {t('user.sidebar.changePassword.title')}
     </Typography>
    </Box>
   )}
   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Box sx={{ position: 'relative' }}>
     <TextField
      type={showPassword ? 'text' : 'password'}
      label={t('user.sidebar.changePassword.password')}
      value={password}
      onChange={(event) => {
       setPassword(event.target.value);
      }}
      autoComplete="new-password"
      fullWidth
      error={hasMismatch}
      slotProps={{
       inputLabel: {
        shrink: password.length > 0,
       },
      }}
     />
     <IconButton
      onClick={() => setShowPassword((prevValue) => !prevValue)}
      title={
       showPassword
        ? t('user.sidebar.changePassword.hidePassword')
        : t('user.sidebar.changePassword.showPassword')
      }
      sx={{
       position: 'absolute',
       top: '23px',
       right: '12px',
       transform: 'translateY(-50%)',
       opacity: 0.5,
       color: (theme: Theme) => theme.palette.kemek.darkGray,
      }}
      aria-label={
       showPassword
        ? t('user.sidebar.changePassword.hidePassword')
        : t('user.sidebar.changePassword.showPassword')
      }
     >
      {showPassword ? <IconHide /> : <IconShow />}
     </IconButton>
    </Box>
    <Box sx={{ position: 'relative' }}>
     <TextField
      type={showRepeatPassword ? 'text' : 'password'}
      label={t('user.sidebar.changePassword.repeatPassword')}
      value={repeatPassword}
      onChange={(event) => {
       setRepeatPassword(event.target.value);
      }}
      autoComplete="new-password"
      fullWidth
      error={hasMismatch}
      helperText={
       hasMismatch ? t('user.sidebar.changePassword.passwordsDoNotMatch') : ''
      }
      slotProps={{
       inputLabel: {
        shrink: repeatPassword.length > 0,
       },
      }}
     />
     <IconButton
      onClick={() => setShowRepeatPassword((prevValue) => !prevValue)}
      title={
       showRepeatPassword
        ? t('user.sidebar.changePassword.hideRepeatPassword')
        : t('user.sidebar.changePassword.showRepeatPassword')
      }
      sx={{
       position: 'absolute',
       top: '23px',
       right: '12px',
       transform: 'translateY(-50%)',
       opacity: 0.5,
       color: (theme: Theme) => theme.palette.kemek.darkGray,
      }}
      aria-label={
       showRepeatPassword
        ? t('user.sidebar.changePassword.hideRepeatPassword')
        : t('user.sidebar.changePassword.showRepeatPassword')
      }
     >
      {showRepeatPassword ? <IconHide /> : <IconShow />}
     </IconButton>
    </Box>
    {hasMismatch && (
     <Alert severity="error" sx={{ mb: 0 }}>
      {t('user.sidebar.changePassword.passwordsDoNotMatch')}
     </Alert>
    )}
    {!hideButtons && (
     <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Button
       variant="text"
       color="secondary"
       onClick={handleCancel}
       disabled={isSubmitting}
      >
       {t('user.sidebar.changePassword.cancel')}
      </Button>
      <Button
       variant="contained"
       onClick={handleSubmit}
       disabled={isSubmitDisabled}
      >
       {t('user.sidebar.changePassword.submit')}
      </Button>
     </Box>
    )}
   </Box>
  </Box>
 );
};

export default ChangePassword;
